import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { translations } from "./translations";
import { logger } from "../lib/logger";
import { api } from "../lib/api";

const I18nContext = createContext(null);

function detectInitialLang() {
  try {
    const saved = localStorage.getItem("lang");
    if (saved === "sv" || saved === "en") return saved;
  } catch (err) {
    logger.warn("i18n: localStorage read failed", err);
  }
  try {
    const nav = (navigator.language || navigator.userLanguage || "").toLowerCase();
    if (nav.startsWith("sv")) return "sv";
    return "en";
  } catch (err) {
    logger.warn("i18n: navigator.language read failed", err);
    return "sv";
  }
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);
  // CMS-driven overrides fetched from /api/i18n/overrides — merged on top of base translations.
  const [overrides, setOverrides] = useState({ sv: {}, en: {} });

  const refreshOverrides = useCallback(async () => {
    try {
      const r = await api.get("/i18n/overrides");
      const o = r.data || {};
      setOverrides({ sv: o.sv || {}, en: o.en || {} });
    } catch (err) {
      logger.warn("i18n: failed to fetch overrides", err);
    }
  }, []);

  useEffect(() => {
    refreshOverrides();
  }, [refreshOverrides]);

  useEffect(() => {
    try {
      localStorage.setItem("lang", lang);
    } catch (err) {
      logger.warn("i18n: localStorage write failed", err);
    }
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => setLangState(l === "en" ? "en" : "sv"), []);

  const t = useCallback(
    (key, vars) => {
      const dict = translations[lang] || translations.sv;
      const ovr = overrides[lang] || {};
      const ovrSv = overrides.sv || {};
      let str =
        ovr[key] ??
        dict[key] ??
        ovrSv[key] ??
        translations.sv[key] ??
        key;
      if (vars && typeof str === "string") {
        for (const [k, v] of Object.entries(vars)) {
          str = str.replace(new RegExp(`\\{${k}\\}`, "g"), v ?? "");
        }
      }
      return str;
    },
    [lang, overrides]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, overrides, refreshOverrides }),
    [lang, setLang, t, overrides, refreshOverrides]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
